import { useCallback, useEffect, useRef } from 'react';
import { logoutUser } from '../api/api';
import { getAccessToken } from '../utils/authSession';

export const INACTIVITY_LOGOUT_MS = 30 * 60 * 1000;

export const INACTIVITY_LOGOUT_MESSAGE =
  'You were signed out after 30 minutes of inactivity. Please sign in again.';

const CHECK_INTERVAL_MS = 60 * 1000;
const THROTTLE_MS = 1000;

export function useInactivityLogout(enabled = true): void {
  const lastActivityRef = useRef(Date.now());
  const throttlingRef = useRef(false);

  const markActive = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const markActiveThrottled = useCallback(() => {
    if (throttlingRef.current) return;
    throttlingRef.current = true;
    lastActivityRef.current = Date.now();
    window.setTimeout(() => {
      throttlingRef.current = false;
    }, THROTTLE_MS);
  }, []);

  useEffect(() => {
    if (!enabled || !getAccessToken()) return;

    markActive();

    const immediateEvents = ['mousedown', 'keydown', 'touchstart', 'pointerdown', 'click'] as const;
    const throttledEvents = ['mousemove', 'scroll', 'wheel'] as const;

    immediateEvents.forEach((event) =>
      window.addEventListener(event, markActive, { passive: true })
    );
    throttledEvents.forEach((event) =>
      window.addEventListener(event, markActiveThrottled, { passive: true })
    );

    const intervalId = window.setInterval(() => {
      if (!getAccessToken()) return;
      if (Date.now() - lastActivityRef.current >= INACTIVITY_LOGOUT_MS) {
        logoutUser(INACTIVITY_LOGOUT_MESSAGE);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      immediateEvents.forEach((event) => window.removeEventListener(event, markActive));
      throttledEvents.forEach((event) => window.removeEventListener(event, markActiveThrottled));
      window.clearInterval(intervalId);
    };
  }, [enabled, markActive, markActiveThrottled]);
}
